<template>
  <div id="homePage1" class="page home">
    <section class="hero">
      <div class="hero__titles">
        <div class="hero__title-line">
          {{ getText(currentIndex, "titleLine1") }}
        </div>
        <div class="hero__title-line hero__title-accent">
          {{ getText(currentIndex, "titleLine2") }}
        </div>
      </div>

      <div class="carousel" @mouseenter="stopAuto" @mouseleave="startAuto">
        <div class="carousel__viewport" @wheel="handleWheel">
          <div
            class="carousel__track"
            :key="localeKey"
            :style="{
              transform: 'translateX(-' + currentIndex * 100 + '%)',
            }"
          >
            <div class="carousel__slide" v-for="(s, i) in slides" :key="i">
              <div class="hero__content">
                <div class="hero__left" :dir="isRtl ? 'rtl' : 'ltr'">
                  <div class="kicker">{{ getText(i, "kicker") }}</div>
                  <h2 class="headline">
                    {{ getText(i, "headline1") }}<br />{{
                      getText(i, "headline2")
                    }}
                  </h2>
                  <ul class="bullets">
                    <li class="bullet">
                      <span class="bullet__dot"></span>
                      <span class="bullet__text">{{
                        getText(i, "bullet1")
                      }}</span>
                    </li>
                    <li class="bullet">
                      <span class="bullet__dot"></span>
                      <span class="bullet__text">{{
                        getText(i, "bullet2")
                      }}</span>
                    </li>
                  </ul>
                </div>

                <div class="hero__right">
                  <div class="slide-img-box">
                    <img v-if="s.src" :src="s.src" alt="" class="slide-img" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="carousel__dots page-dots">
        <button
          v-for="(s, i) in slides"
          :key="'dot' + i"
          class="dot"
          :class="{ active: i === currentIndex }"
          @click="goTo(i)"
          :aria-label="$t('hp1.goToSlide')"
        ></button>
      </div>
    </section>
  </div>
</template>

<script>
import a1 from "@/assets/img/homePage/3-1.png"
import a2 from "@/assets/img/homePage/3-2.png"
import a3 from "@/assets/img/homePage/3-3.png"
import a4 from "@/assets/img/homePage/3-4.png"

export default {
  components: {},
  data() {
    return {
      slides: [{ src: a1 }, { src: a2 }, { src: a3 }, { src: a4 }],
      slideTextKeys: [
        {
          titleLine1: "hp1.titleLine1",
          titleLine2: "hp1.titleLine2",
          kicker: "hp1.kicker",
          headline1: "hp1.headline1",
          headline2: "hp1.headline2",
          bullet1: "hp1.bullet1",
          bullet2: "hp1.bullet2",
        },
        {
          titleLine1: "hp1.titleLine1_2",
          titleLine2: "hp1.titleLine2_2",
          kicker: "hp1.kicker_2",
          headline1: "hp1.headline1_2a",
          headline2: "hp1.headline1_2b",
          bullet1: "hp1.bullet1_2",
          bullet2: "hp1.bullet2_2",
        },
        {
          titleLine1: "hp1.titleLine1_3",
          titleLine2: "hp1.titleLine2_3",
          kicker: "hp1.kicker_3",
          headline1: "hp1.headline1_3a",
          headline2: "hp1.headline1_3b",
          bullet1: "hp1.bullet1_3",
          bullet2: "hp1.bullet2_3",
        },
        {
          titleLine1: "hp1.titleLine1_4",
          titleLine2: "hp1.titleLine2_4",
          kicker: "hp1.kicker_4",
          headline1: "hp1.headline1_4a",
          headline2: "hp1.headline1_4b",
          bullet1: "hp1.bullet1_4",
          bullet2: "hp1.bullet2_4",
        },
      ],
      currentIndex: 0,
      autoplayIntervalMs: 4000,
      timerId: null,
      wheelThrottle: false,
    }
  },
  mounted() {
    this.startAuto()
    document.addEventListener("visibilitychange", this.onVisibilityChange)
  },
  beforeDestroy() {
    this.cleanup()
  },

  beforeUnmount() {
    this.cleanup()
  },
  computed: {
    localeKey() {
      const i18n = this.$i18n
      if (!i18n || !i18n.locale) return "default"
      const loc = i18n.locale
      // vue-i18n v9 may expose locale as a Ref
      if (typeof loc === "object" && "value" in loc)
        return loc.value || "default"
      return loc
    },
    isRtl() {
      const locale = String(this.localeKey || "").toLowerCase()
      const primary = locale.split("-")[0]
      const rtlLocales = [
        "ar",
        "fa",
        "ur",
        "he",
        "ug",
        "ps",
        "ku",
        "sd",
        "dv",
        "yi",
      ]
      return rtlLocales.includes(primary)
    },
  },
  watch: {
    localeKey() {
      this.stopAuto()
      // 移除重置currentIndex的逻辑，保持当前轮播位置
      this.$nextTick(() => {
        this.startAuto()
      })
    },
  },
  methods: {
    getText(index, field) {
      const base = {
        titleLine1: "hp1.titleLine1",
        titleLine2: "hp1.titleLine2",
        kicker: "hp1.kicker",
        headline1: "hp1.headline1",
        headline2: "hp1.headline2",
        bullet1: "hp1.bullet1",
        bullet2: "hp1.bullet2",
      }
      const keys = this.slideTextKeys[index] || base
      const value = keys[field]

      if (!value) {
        return this.$t(base[field])
      }

      // 直接使用翻译键进行翻译
      return this.$t(value)
    },
    next() {
      const lastIndex = this.slides.length - 1
      // 如果是RTL语言（如乌尔都语），则反向轮播
      if (this.isRtl) {
        this.currentIndex =
          this.currentIndex <= 0 ? lastIndex : this.currentIndex - 1
      } else {
        this.currentIndex =
          this.currentIndex >= lastIndex ? 0 : this.currentIndex + 1
      }
    },
    prev() {
      const lastIndex = this.slides.length - 1
      // 如果是RTL语言（如乌尔都语），则反向轮播
      if (this.isRtl) {
        this.currentIndex =
          this.currentIndex >= lastIndex ? 0 : this.currentIndex + 1
      } else {
        this.currentIndex =
          this.currentIndex <= 0 ? lastIndex : this.currentIndex - 1
      }
    },
    goTo(index) {
      if (index >= 0 && index < this.slides.length) this.currentIndex = index
    },
    handleWheel(event) {
      event.preventDefault()

      if (this.wheelThrottle) return

      this.wheelThrottle = true
      setTimeout(() => {
        this.wheelThrottle = false
      }, 300)

      // 如果是RTL语言，则反向处理滚轮方向
      if (this.isRtl) {
        if (event.deltaY > 0) {
          this.prev()
        } else {
          this.next()
        }
      } else {
        if (event.deltaY > 0) {
          this.next()
        } else {
          this.prev()
        }
      }
    },
    startAuto() {
      this.stopAuto()
      this.timerId = setInterval(this.next, this.autoplayIntervalMs)
    },
    stopAuto() {
      if (this.timerId) {
        clearInterval(this.timerId)
        this.timerId = null
      }
    },
    onVisibilityChange() {
      if (document.hidden) this.stopAuto()
      else this.startAuto()
    },
    cleanup() {
      this.stopAuto()
      document.removeEventListener("visibilitychange", this.onVisibilityChange)
    },
  },
}
</script>

<style scoped>
.page.home {
  /* background: #f5f8ff; */
  color: #0b0b14;
}

.hero {
  position: relative;
  padding: 48px 24px 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.hero__titles {
  text-align: center;
  margin-bottom: 28px;
}

.hero__title-line {
  font-size: 40px;
  font-weight: 700;
  color: #1a1a2b;
}

.hero__title-accent {
  color: #24243e;
}

.hero__content {
  display: grid;
  grid-template-columns: 1fr 1.1fr;
  gap: 24px;
  align-items: center;
}

.hero__left {
  padding: 16px 12px;
}

.kicker {
  color: #3056ff;
  font-weight: 600;
  font-size: 24px;
  margin-bottom: 30px;
}

.headline {
  font-size: 40px;
  line-height: 1.35;
  margin: 0 0 16px;
  color: #12122b;
}

.bullets {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 12px;
}

.bullet {
  display: grid;
  grid-template-columns: 10px 1fr;
  align-items: start;
  gap: 10px;
}

.bullet__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #6aa6ff, #3056ff);
  margin-top: 6px;
}

.bullet__text {
  color: #404056;
  font-size: 18px;
  line-height: 1.7;
}

.hero__right {
  position: relative;
  display: flex;
  justify-content: center;
}

.carousel {
  position: relative;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

.carousel__viewport {
  overflow: hidden;
  border-radius: 0;
  box-shadow: none;
  background: transparent;
  transition: box-shadow 300ms ease;
}

.carousel__viewport:hover {
  box-shadow: none;
}

.carousel__track {
  display: flex;
  width: 100%;
  transition: transform 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.carousel__viewport,
.carousel__track {
  direction: ltr !important;
}
/* RTL overrides - 保持从左到右的轮播方向 */
[dir="rtl"] .carousel__viewport,
[dir="rtl"] .carousel__track {
  direction: ltr !important;
}

.carousel__slide {
  min-width: 100%;
  padding: 18px;
  transition: transform 600ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.carousel__slide:hover {
  transform: scale(1.02);
}

.slide-img-box {
  position: relative;
  height: 300px;
  border-radius: 0;
  background: transparent;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slide-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.carousel__dots {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 16px;
}

.carousel__dots .dot {
  width: 6px;
  height: 6px;
  border-radius: 6px;
  background: #cfd6ff;
  border: none;
  cursor: pointer;
}

.carousel__dots .dot.active {
  background: #3056ff;
}

@media (min-width: 961px) {
  .hero {
    padding-bottom: 96px;
  }
  .page-dots {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: 16px;
    margin-top: 0;
    z-index: 2;
  }

  .hero__content {
    grid-template-columns: 1fr 1.4fr;
  }

  .hero__right {
    width: 640px;
  }
  .slide-img-box {
    width: 640px;
    height: 380px;
  }
}

@media (max-width: 960px) {
  .hero__content {
    grid-template-columns: 1fr;
  }

  /* 移动端文字大小优化 */
  .hero__title-line {
    font-size: 24px;
  }

  .kicker {
    font-size: 13px;
    margin-bottom: 20px;
  }

  .headline {
    font-size: 24px;
    line-height: 1.3;
    margin-bottom: 12px;
  }

  .bullet__text {
    font-size: 13px;
    line-height: 1.6;
  }

  .hero {
    padding: 32px 16px 16px;
  }

  .hero__left {
    padding: 12px 8px;
  }

  .carousel__slide {
    padding: 12px;
  }
}

/* 小屏幕手机优化 */
@media (max-width: 480px) {
  .hero__title-line {
    font-size: 20px;
  }

  .kicker {
    font-size: 12px;
    margin-bottom: 16px;
  }

  .headline {
    font-size: 20px;
    line-height: 1.25;
    margin-bottom: 10px;
  }

  .bullet__text {
    font-size: 12px;
    line-height: 1.5;
  }

  .hero {
    padding: 24px 12px 12px;
  }

  .hero__titles {
    margin-bottom: 20px;
  }

  .bullets {
    gap: 8px;
  }

  .bullet {
    gap: 8px;
  }

  .bullet__dot {
    width: 8px;
    height: 8px;
    margin-top: 4px;
  }
}
</style>
